import {
  Box,
  BoxProps,
  InputProps,
  List,
  ListItem,
} from '@chakra-ui/react'
import * as React from 'react'
import { BodyBackgroundNuance, useColor } from '../../../theme'
import { IFormValidationProps } from '../hooks'
import { TComboboxOption } from '../types'
import { useAuthLabelStyle } from '../useAuthLabelStyle'
import { ErrorMessage } from '../ErrorMessage'
import { ComboboxInput } from './ComboboxInput'
import { useComboboxControls } from './useComboboxControls'

type ComboboxProps = IFormValidationProps & Omit<BoxProps, 'onSelect'> & Pick<InputProps, 'placeholder' | 'size' | 'variant'> & {
  defaultOption?: TComboboxOption
  isDisabled?: boolean
  noResultsMessage: string
  options: TComboboxOption[]
  onSelect: (option: TComboboxOption | null) => void
  icon?: JSX.Element
  readonly?: boolean
  placement?: 'bottom' | 'top'
}


export const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(
  (
    {
      defaultOption,
      isDisabled = false,
      isInvalid,
      errorMessage,
      Label,
      noResultsMessage,
      options,
      onSelect,
      placeholder,
      size,
      icon,
      variant,
      readonly,
      placement = 'bottom',
      ...boxProps
    },
    ref,
  ) => {

    const listItemColor = useColor({ nuance: BodyBackgroundNuance.Clean, palette: 'body' })
    const listItemHoverColor = useColor({ nuance: 0, palette: 'secondary' })
    const textColor = useColor({ nuance: 1, palette: 'text' })
    const textHoverColor = useColor({ nuance: 6, palette: 'text' })

    const { controls, innerOptions, inputValue, setInnerOptions, setInputValue, setSelectedOption } = useComboboxControls({ options, defaultOption, onSelect })

    // #region isAuth variant
    const isAuth = React.useMemo(() => variant === 'auth', [variant])

    const authLabelStyle = useAuthLabelStyle()

    const [isFocused, setIsFocused] = React.useState(false)

    const onBlur = React.useCallback(() => setIsFocused(false), [])

    const showLabel = React.useMemo(
      () => (isAuth ? controls.isOpen || isFocused || !!inputValue : true),
      [controls.isOpen, isFocused, isAuth, inputValue],
    )

    const authBackgroundColor = useColor({ nuance: BodyBackgroundNuance.Clean, palette: 'body' })

    const authTextColor = useColor({
      nuance: 6,
      palette: 'text',
    })
    // #endregion

    return (
      <Box
        onBlur={onBlur}
        position="relative"
        ref={ref}
        {...boxProps}
        {...controls.getComboboxProps()}
        {...(isDisabled ? { cursor: 'not-allowed' } : {})}>
        {Label && showLabel && (
          <Label {...controls.getLabelProps()} {...(isAuth ? authLabelStyle : {})} />
        )}
        <ComboboxInput controls={controls} icon={icon} inputValue={inputValue} isAuth={isAuth} isDisabled={isDisabled} isInvalid={isInvalid} onSelect={onSelect} options={options} placeholder={placeholder} readonly={readonly} setInnerOptions={setInnerOptions} setInputValue={setInputValue} setIsFocused={setIsFocused} setSelectedOption={setSelectedOption} size={size} variant={variant} {...controls.getMenuProps({}, { suppressRefError: true })} />
        {controls.isOpen && (
          <Box
            backgroundColor={isAuth ? authBackgroundColor : listItemColor}
            borderRadius="1rem"
            boxShadow="0px 0px 20px rgba(0, 0, 0, 0.1)"
            position="absolute"
            py={2}
            sx={{ width: '100%' }}
            zIndex="dropdown"
            {...(placement === 'top'
              ? { bottom: '100%', marginBottom: '0.5rem' }
              : { marginTop: '0.5rem' })}>
            <Box maxHeight="200px" overflow="auto">
              <List
                css={{
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: '0px',
                  },
                }}
                data-testid="combobox-list"
                overflowY="auto"
                padding={1.5}
                position="relative"
                size={size}
              >
                {innerOptions.map((option, index) => (
                  <ListItem
                    _hover={{
                      backgroundColor: listItemHoverColor,
                      color: textHoverColor,
                    }}
                    _selected={{
                      color: textHoverColor,
                    }}
                    backgroundColor={
                      controls.highlightedIndex === index
                        ? listItemHoverColor
                        : 'transparent'
                    }
                    borderRadius="0.5rem"
                    color={textColor}
                    cursor="pointer"
                    data-testid={`combobox-list-item-${option.label}`}
                    fontSize="md"
                    fontWeight="500"
                    key={option.id}
                    padding="0.5rem"
                    {...controls.getItemProps({ index, item: option })}>
                    {option.label}
                  </ListItem>
                ))}
                {innerOptions.length === 0 && (
                  <ListItem
                    color={isAuth ? authTextColor : textColor}
                    data-testid="combobox-list-no-results"
                    fontSize="md"
                    fontWeight="500"
                    padding="0.3rem"
                    paddingLeft="1rem">
                    {noResultsMessage}
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
        )}
        {errorMessage && (
          <ErrorMessage isDisabled={isDisabled}>{errorMessage}</ErrorMessage>
        )}
      </Box>
    )
  },
)
